import { useState } from 'react';
import { Button, Center, Group, Loader, SimpleGrid, Stack, Text } from '@mantine/core';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useChecklist, useProjectAnalyses } from '@/api/analyses';
import { errorMessage } from '@/api/errors';
import { useProjectObjects, type DataObject } from '@/api/objects';
import { canShare, canUpload, canViewContents, useProject, useShares } from '@/api/projects';
import { useProjectReports } from '@/api/reports';
import { EmptyState } from '@/components/EmptyState';
import { SectionDivider } from '@/components/SectionDivider';
import { usePageTitle } from '@/components/usePageTitle';
import { AnalysisCard } from '@/features/analyses/AnalysisCard';
import { DeleteObjectModal } from '@/features/objects/DeleteObjectModal';
import { ObjectsByOrigin } from '@/features/objects/ObjectsByOrigin';
import { ObjectsTable } from '@/features/objects/ObjectsTable';
import { RegisterObjectModal } from '@/features/objects/RegisterObjectModal';
import { Checklist } from '@/features/projects/Checklist';
import { ReportsTable } from '@/features/reports/ReportsTable';
import { ShareModal } from '@/features/projects/ShareModal';
import { SharesTable } from '@/features/projects/SharesTable';
import { ClientShell } from '@/shells/ClientShell';
import { brand } from '@/theme/theme';

export const Route = createFileRoute('/_client/projects/$projectId')({ component: ProjectPage });

function ProjectPage() {
  const { t } = useTranslation('projects');
  const { t: ta } = useTranslation('analyses');
  const { t: tr } = useTranslation('reports');
  const { projectId } = Route.useParams();
  const project = useProject(projectId);
  const objects = useProjectObjects(projectId);
  const role = project.data?.role;
  const shares = useShares(projectId, canShare(role));
  const checklist = useChecklist(projectId, !!role);
  const analyses = useProjectAnalyses(projectId, canViewContents(role));
  const reports = useProjectReports(projectId, canViewContents(role));
  const [presetKind, setPresetKind] = useState<string | null>(null);
  const navigate = useNavigate();
  const [registerOpen, setRegisterOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [toDelete, setToDelete] = useState<DataObject | null>(null);
  usePageTitle(project.data?.name ?? t('project.data'));

  if (project.isPending) {
    return (
      <ClientShell title="…">
        <Center py="xl">
          <Loader color="forest" />
        </Center>
      </ClientShell>
    );
  }
  if (project.isError) {
    return (
      <ClientShell title={t('project.data')}>
        <EmptyState title={errorMessage(project.error)} />
      </ClientShell>
    );
  }
  const p = project.data;
  const active = p.state === 'active';
  const mayUpload = canUpload(role) && active;

  return (
    <ClientShell
      eyebrow={p.organization.name}
      title={p.name}
      lead={t(`status.project.${p.client_status}`, { defaultValue: p.client_status, delivered: p.analyses_summary.delivered, total: p.analyses_summary.total })}
      cta={mayUpload ? { label: t('project.uploadCta'), onClick: () => setRegisterOpen(true) } : undefined}
    >
      <Stack gap="lg">
        <SectionDivider label={t('project.checklist')} />
        {checklist.isPending ? (
          <Center py="md">
            <Loader color="forest" size="sm" />
          </Center>
        ) : (
          <Checklist
            items={checklist.data ?? []}
            variant={canViewContents(role) ? 'full' : 'uploader'}
            onUpload={mayUpload ? (kind) => { setPresetKind(kind); setRegisterOpen(true); } : undefined}
            onContinue={mayUpload ? (objectId) => void navigate({ to: '/projects/$projectId/upload/$objectId', params: { projectId, objectId } }) : undefined}
          />
        )}
        {canViewContents(role) && (
          <>
            <SectionDivider label={t('project.analyses')} />
            {analyses.isPending ? (
              <Center py="md">
                <Loader color="forest" size="sm" />
              </Center>
            ) : (analyses.data ?? []).length === 0 ? (
              <EmptyState title={ta('empty')} description={ta('emptyHint')} />
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                {(analyses.data ?? []).map((a) => (
                  <AnalysisCard key={a.public_id} analysis={a} projectId={projectId} />
                ))}
              </SimpleGrid>
            )}
            <SectionDivider label={tr('title')} />
            {reports.isPending ? (
              <Center py="md">
                <Loader color="forest" size="sm" />
              </Center>
            ) : (reports.data ?? []).length === 0 ? (
              <EmptyState title={tr('empty')} description={tr('emptyHint')} />
            ) : (
              <ReportsTable projectId={projectId} reports={reports.data ?? []} />
            )}
          </>
        )}
        <SectionDivider label={t('project.data')} />
        {!active && (
          <Text fz={14} c={brand.textMuted}>
            {t('project.closedHint')}
          </Text>
        )}
        {objects.isPending ? (
          <Center py="xl">
            <Loader color="forest" />
          </Center>
        ) : objects.isError ? (
          <EmptyState title={errorMessage(objects.error)} />
        ) : objects.data.length === 0 ? (
          <EmptyState
            title={t('objects.empty')}
            description={t('objects.emptyHint')}
            action={mayUpload ? { label: t('objects.register'), onClick: () => setRegisterOpen(true) } : undefined}
          />
        ) : (
          <Stack gap="sm">
            {mayUpload && (
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setRegisterOpen(true)}>
                  {t('objects.register')}
                </Button>
              </Group>
            )}
            <ObjectsByOrigin objects={objects.data}>
              {(group) => (
                <ObjectsTable projectId={projectId} objects={group} canUpload={canUpload(role)} projectActive={active} onDelete={setToDelete} />
              )}
            </ObjectsByOrigin>
          </Stack>
        )}

        {canShare(role) && (
          <>
            <SectionDivider label={t('project.sharing')} />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setShareOpen(true)}>
                {t('share.add')}
              </Button>
            </Group>
            {shares.isPending ? (
              <Center py="md">
                <Loader color="forest" size="sm" />
              </Center>
            ) : (
              <SharesTable projectId={projectId} shares={shares.data ?? []} />
            )}
            <ShareModal projectId={projectId} opened={shareOpen} onClose={() => setShareOpen(false)} />
          </>
        )}
      </Stack>
      <RegisterObjectModal key={presetKind ?? 'none'} projectId={projectId} opened={registerOpen} onClose={() => { setRegisterOpen(false); setPresetKind(null); }} existing={objects.data ?? []} presetKind={presetKind} />
      <DeleteObjectModal object={toDelete} onClose={() => setToDelete(null)} />
    </ClientShell>
  );
}
